import { BedrockRuntimeClient, InvokeModelCommand } from '@aws-sdk/client-bedrock-runtime';
import { AWS_CONFIG, SUPPORTED_LANGUAGES, BEDROCK_MODELS, DEFAULT_MODEL } from '../utils/awsConfig';

export class TranslationService {
  constructor(selectedModel = DEFAULT_MODEL, credentials = null, existingTokenUsage = null) {
    console.log('🔵 Initializing TranslationService...');
    console.log('🔵 Region:', AWS_CONFIG.region);
    console.log('🔵 Selected model:', selectedModel);
    console.log('🔵 Credentials provided:', !!credentials);
    
    // Store the selected model
    this.selectedModel = selectedModel;
    this.modelConfig = BEDROCK_MODELS[selectedModel];
    
    // Store custom system prompt and reasoning preference
    this.customSystemPrompt = null;
    this.showReasoning = false;
    
    if (!this.modelConfig) {
      console.error('🔴 Invalid model selected:', selectedModel);
      throw new Error(`Invalid model selected: ${selectedModel}`);
    }
    
    console.log('🟢 Using model:', this.modelConfig.name, `(${this.modelConfig.provider})`);
    
    // Initialize or preserve token tracking
    if (existingTokenUsage) {
      console.log('🔵 Preserving existing token usage');
      this.tokenUsage = existingTokenUsage;
    } else {
      console.log('🔵 Initializing new token usage tracking');
      this.tokenUsage = {
        totalInputTokens: 0,
        totalOutputTokens: 0,
        totalRequests: 0,
        sessionStartTime: new Date(),
        requestHistory: []
      };
    }
    
    // Initialize per-model token tracking
    if (!this.tokenUsage.perModel) {
      this.tokenUsage.perModel = {};
    }
    
    // Initialize current model tracking if not exists
    if (!this.tokenUsage.perModel[selectedModel]) {
      this.tokenUsage.perModel[selectedModel] = {
        inputTokens: 0,
        outputTokens: 0,
        requests: 0,
        totalCost: 0,
        modelName: this.modelConfig.name
      };
    }
    
    // Use credentials from Cognito or fallback to environment variables
    let clientCredentials;
    
    if (credentials && credentials.accessKeyId && credentials.secretAccessKey) {
      console.log('🔵 Using Cognito credentials');
      clientCredentials = {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      };
    } else {
      console.log('🔵 Using environment variables');
      if (!process.env.REACT_APP_AWS_ACCESS_KEY_ID || !process.env.REACT_APP_AWS_SECRET_ACCESS_KEY) {
        console.error('🔴 AWS credentials not found!');
        throw new Error('AWS credentials not configured. Please check your authentication.');
      }
      
      clientCredentials = {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
      };
    }
    
    // Initialize Bedrock client
    this.client = new BedrockRuntimeClient({
      region: AWS_CONFIG.region,
      credentials: clientCredentials
    });
    
    console.log('🟢 TranslationService initialized successfully');
  }

  // Update the model configuration
  updateModel(newModel) {
    console.log('🔵 Updating model from', this.selectedModel, 'to', newModel);
    this.selectedModel = newModel;
    this.modelConfig = BEDROCK_MODELS[newModel];
    
    if (!this.modelConfig) {
      throw new Error(`Invalid model selected: ${newModel}`);
    }
    
    console.log('🟢 Model updated to:', this.modelConfig.name, `(${this.modelConfig.provider})`);
  }

  // NEW: Update custom system prompt (minimal addition)
  updateSystemPrompt(newSystemPrompt) {
    console.log('🔵 Updating system prompt');
    this.customSystemPrompt = newSystemPrompt;
    console.log('🟢 System prompt updated');
  }

  // Set reasoning display preference
  setShowReasoning(show) {
    this.showReasoning = show;
    console.log('🔵 Show reasoning set to:', show);
  }

  // Test connection to AWS Bedrock
  async testConnection() {
    try {
      console.log('🔵 Testing Bedrock connection...');
      
      // Use Claude 3 Haiku for connection test (most reliable)
      const testModelId = 'anthropic.claude-3-haiku-20240307-v1:0';
      
      const testPayload = {
        anthropic_version: "bedrock-2023-05-31",
        max_tokens: 100,
        messages: [
          {
            role: "user",
            content: "Translate 'Hello' to Thai. Respond with only the translation."
          }
        ]
      };

      const command = new InvokeModelCommand({
        modelId: testModelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(testPayload)
      });

      console.log('🔵 Testing with Claude 3 Haiku...');
      const response = await this.client.send(command);
      const responseBody = JSON.parse(new TextDecoder().decode(response.body));
      
      if (responseBody && responseBody.content && responseBody.content[0] && responseBody.content[0].text) {
        console.log('🟢 Bedrock connection test successful');
        return true;
      } else {
        console.log('🔴 Bedrock connection test failed - empty response');
        return false;
      }
    } catch (error) {
      console.error('🔴 Bedrock connection test failed:', error);
      return false;
    }
  }

  // Test model availability
  async testModelAccess() {
    try {
      console.log('🔵 Testing model access for:', this.modelConfig.modelId);
      
      const testPayload = {
        anthropic_version: "bedrock-2023-05-31",
        max_tokens: 10,
        messages: [
          {
            role: "user",
            content: "Hi"
          }
        ]
      };

      const command = new InvokeModelCommand({
        modelId: this.modelConfig.modelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(testPayload)
      });

      await this.client.send(command);
      console.log('✅ Model access confirmed for:', this.modelConfig.name);
      return true;
    } catch (error) {
      console.error('❌ Model access failed:', error.name, error.message);
      if (error.name === 'AccessDeniedException') {
        throw new Error(`No access to ${this.modelConfig.name}. Please check IAM permissions for bedrock:InvokeModel.`);
      } else if (error.name === 'ValidationException') {
        throw new Error(`${this.modelConfig.name} model not found or not available in your region.`);
      }
      throw error;
    }
  }
  async translateText(text, sourceLanguage, targetLanguage, isRealTime = false) {
    try {
      console.log('🔵 Starting translation...');
      console.log('🔵 Text:', text);
      console.log('🔵 From:', sourceLanguage, 'To:', targetLanguage);
      console.log('🔵 Real-time:', isRealTime);
      console.log('🔵 Using model:', this.modelConfig.name);
      
      const sourceLangName = SUPPORTED_LANGUAGES[sourceLanguage]?.name || 'English';
      const targetLangName = SUPPORTED_LANGUAGES[targetLanguage]?.name || 'English';

      // Check if text contains speaker labels and timestamps
      const hasSpeakerLabels = text.includes('[SPK_') && text.includes(']:');
      
      // Create prompt based on model provider
      let payload;
      
      if (this.modelConfig.provider === 'Anthropic') {
        // Claude models use the messages format
        let prompt;
        
        if (this.customSystemPrompt) {
          // Use custom system prompt if available
          if (hasSpeakerLabels) {
            prompt = `${this.customSystemPrompt}\n\nIMPORTANT: The text contains speaker labels and timestamps in format [SPK_X MM:SS]. Please preserve this exact formatting in your translation.\n\nText to translate:\n${text}`;
          } else {
            prompt = `${this.customSystemPrompt}\n\nText to translate: "${text}"`;
          }
        } else {
          // Use original default prompt
          if (hasSpeakerLabels) {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon.\n\n${text}`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon. Maintain the same line breaks and formatting.\n\n${text}`;
          } else {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text might be incomplete. Provide the best translation possible: "${text}"`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. Only provide the translation, no explanations or additional text.\n\nText: "${text}"`;
          }
        }
        
        // Determine anthropic_version based on model
        const anthropicVersion = this.modelConfig.modelId.includes('claude-4') || this.modelConfig.modelId.includes('opus-4') 
          ? "bedrock-2023-05-31"  // Claude 4 models use same version
          : this.modelConfig.modelId.includes('20241022') || this.modelConfig.modelId.includes('v2:0') 
          ? "bedrock-2023-05-31" 
          : "bedrock-2023-05-31";
        
        // Ensure max_tokens is within valid range for Claude models
        const maxTokens = Math.min(8192, Math.max(1, this.modelConfig.maxTokens || 1000));
        
        // For Claude 4 and newer models, use system message format when available
        const isNewerModel = this.modelConfig.modelId.includes('claude-4') || this.modelConfig.modelId.includes('opus-4') || this.modelConfig.modelId.includes('20241022') || this.modelConfig.modelId.includes('v2:0');
        
        if (isNewerModel && this.customSystemPrompt) {
          payload = {
            anthropic_version: anthropicVersion,
            max_tokens: maxTokens,
            system: this.customSystemPrompt,
            messages: [
              {
                role: "user",
                content: hasSpeakerLabels 
                  ? `IMPORTANT: The text contains speaker labels and timestamps in format [SPK_X MM:SS]. Please preserve this exact formatting in your translation.\n\nText to translate:\n${text}`
                  : `Text to translate: "${text}"`
              }
            ]
          };
        } else {
          payload = {
            anthropic_version: anthropicVersion,
            max_tokens: maxTokens,
            messages: [
              {
                role: "user",
                content: prompt
              }
            ]
          };
        }
      } else if (this.modelConfig.provider === 'Amazon') {
        // Titan and Nova models use different formats
        let prompt;
        
        if (this.customSystemPrompt) {
          // Use custom system prompt if available
          if (hasSpeakerLabels) {
            prompt = `${this.customSystemPrompt}\n\nIMPORTANT: The text contains speaker labels and timestamps in format [SPK_X MM:SS]. Please preserve this exact formatting in your translation.\n\nText to translate:\n${text}`;
          } else {
            prompt = `${this.customSystemPrompt}\n\nText to translate: "${text}"`;
          }
        } else {
          // Use original default prompt
          if (hasSpeakerLabels) {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon.\n\n${text}`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon. Maintain the same line breaks and formatting.\n\n${text}`;
          } else {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text might be incomplete. Provide the best translation possible: "${text}"`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. Only provide the translation, no explanations or additional text.\n\nText: "${text}"`;
          }
        }

        if (this.modelConfig.modelId.includes('nova')) {
          // Nova models use messages format like Claude
          payload = {
            messages: [
              {
                role: "user",
                content: [
                  {
                    text: prompt
                  }
                ]
              }
            ],
            inferenceConfig: {
              maxTokens: Math.min(1000, this.modelConfig.maxTokens),
              temperature: 0.1
            }
          };
        } else {
          // Titan models use the original format
          payload = {
            inputText: prompt,
            textGenerationConfig: {
              maxTokenCount: Math.min(1000, this.modelConfig.maxTokens),
              temperature: 0.1,
              topP: 0.9
            }
          };
        }
      } else if (this.modelConfig.provider === 'OpenAI') {
        // OpenAI models use messages format
        let prompt;
        
        if (this.customSystemPrompt) {
          // Use custom system prompt if available
          if (hasSpeakerLabels) {
            prompt = `${this.customSystemPrompt}\n\nIMPORTANT: The text contains speaker labels and timestamps in format [SPK_X MM:SS]. Please preserve this exact formatting in your translation.\n\nText to translate:\n${text}`;
          } else {
            prompt = `${this.customSystemPrompt}\n\nText to translate: "${text}"`;
          }
        } else {
          // Use original default prompt
          if (hasSpeakerLabels) {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon.\n\n${text}`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon. Maintain the same line breaks and formatting.\n\n${text}`;
          } else {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text might be incomplete. Provide the best translation possible: "${text}"`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. Only provide the translation, no explanations or additional text.\n\nText: "${text}"`;
          }
        }
        
        payload = {
          messages: [
            {
              role: "user",
              content: prompt
            }
          ],
          max_tokens: Math.min(1000, this.modelConfig.maxTokens),
          temperature: 0.1,
          top_p: 0.9
        };
      } else {
        // For other providers (Meta, Mistral, Cohere, DeepSeek, etc.), use messages format
        let prompt;
        
        if (this.customSystemPrompt) {
          // Use custom system prompt if available
          if (hasSpeakerLabels) {
            prompt = `${this.customSystemPrompt}\n\nIMPORTANT: The text contains speaker labels and timestamps in format [SPK_X MM:SS]. Please preserve this exact formatting in your translation.\n\nText to translate:\n${text}`;
          } else {
            prompt = `${this.customSystemPrompt}\n\nText to translate: "${text}"`;
          }
        } else {
          // Use original default prompt
          if (hasSpeakerLabels) {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon.\n\n${text}`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. The text contains speaker labels and timestamps in format [SPK_X MM:SS]. PRESERVE the exact speaker labels and timestamps in your translation. Only translate the spoken content after the colon. Maintain the same line breaks and formatting.\n\n${text}`;
          } else {
            prompt = isRealTime 
              ? `Translate the following ${sourceLangName} text to ${targetLangName} in real-time. The text might be incomplete. Provide only the translation: "${text}"`
              : `Translate the following text from ${sourceLangName} to ${targetLangName}. Provide only the translation, no explanations.\n\nText: "${text}"`;
          }
        }
        
        // Different payload formats for different providers
        if (this.modelConfig.provider === 'Meta' || this.modelConfig.provider === 'Mistral AI') {
          // Meta Llama and Mistral use standard messages format
          payload = {
            messages: [
              {
                role: "user",
                content: prompt
              }
            ],
            max_tokens: Math.min(1000, this.modelConfig.maxTokens),
            temperature: 0.1,
            top_p: 0.9
          };
        } else if (this.modelConfig.provider === 'DeepSeek') {
          // DeepSeek uses messages format with specific parameters
          payload = {
            messages: [
              {
                role: "user",
                content: prompt
              }
            ],
            max_tokens: Math.min(1000, this.modelConfig.maxTokens),
            temperature: 0.1,
            top_p: 0.9,
            stream: false
          };
        } else {
          // Default format for other providers
          payload = {
            messages: [
              {
                role: "user",
                content: prompt
              }
            ],
            max_tokens: Math.min(1000, this.modelConfig.maxTokens),
            temperature: 0.1,
            top_p: 0.9
          };
        }
      }

      console.log('🔵 Payload prepared for', this.modelConfig.provider);

      // Invoke the model
      const command = new InvokeModelCommand({
        modelId: this.modelConfig.modelId,
        contentType: 'application/json',
        accept: 'application/json',
        body: JSON.stringify(payload)
      });

      console.log('🔵 Invoking Bedrock model...');
      const response = await this.client.send(command);
      
      // Parse response
      const responseBody = JSON.parse(new TextDecoder().decode(response.body));
      console.log('🔵 Raw response:', responseBody);

      let translatedText;
      let inputTokens = 0;
      let outputTokens = 0;

      if (this.modelConfig.provider === 'Anthropic') {
        // Claude response format
        translatedText = responseBody.content[0].text;
        inputTokens = responseBody.usage?.input_tokens || 0;
        outputTokens = responseBody.usage?.output_tokens || 0;
      } else if (this.modelConfig.provider === 'Amazon') {
        // Titan and Nova response format
        if (this.modelConfig.modelId.includes('nova')) {
          // Nova format (similar to Anthropic)
          translatedText = responseBody.output?.message?.content?.[0]?.text;
          inputTokens = responseBody.usage?.inputTokens || 0;
          outputTokens = responseBody.usage?.outputTokens || 0;
        } else {
          // Titan format
          translatedText = responseBody.results[0].outputText;
          inputTokens = responseBody.inputTextTokenCount || 0;
          outputTokens = responseBody.results[0].tokenCount || 0;
        }
      } else if (this.modelConfig.provider === 'OpenAI') {
        // OpenAI response format
        if (responseBody.choices && responseBody.choices[0] && responseBody.choices[0].message) {
          let rawText = responseBody.choices[0].message.content;
          
          // Remove reasoning tags if showReasoning is false
          if (!this.showReasoning && rawText.includes('<reasoning>') && rawText.includes('</reasoning>')) {
            // Extract text after </reasoning> tag
            const reasoningEnd = rawText.indexOf('</reasoning>');
            translatedText = rawText.substring(reasoningEnd + 12).trim();
            
            // Remove any remaining quotes at the beginning
            if (translatedText.startsWith('"') && translatedText.endsWith('"')) {
              translatedText = translatedText.slice(1, -1);
            }
          } else {
            translatedText = rawText;
          }
          
          inputTokens = responseBody.usage?.prompt_tokens || 0;
          outputTokens = responseBody.usage?.completion_tokens || 0;
        } else {
          // Fallback for OpenAI
          translatedText = responseBody.text || JSON.stringify(responseBody);
          console.warn('🟡 Unexpected OpenAI response format');
        }
      } else {
        // For other providers (Meta, Mistral, Cohere, etc.), try common formats
        if (responseBody.content && responseBody.content[0] && responseBody.content[0].text) {
          // Anthropic-like format
          translatedText = responseBody.content[0].text;
          inputTokens = responseBody.usage?.input_tokens || 0;
          outputTokens = responseBody.usage?.output_tokens || 0;
        } else if (responseBody.choices && responseBody.choices[0] && responseBody.choices[0].message) {
          // OpenAI-like format
          translatedText = responseBody.choices[0].message.content;
          inputTokens = responseBody.usage?.prompt_tokens || 0;
          outputTokens = responseBody.usage?.completion_tokens || 0;
        } else if (responseBody.outputs && responseBody.outputs[0]) {
          // Cohere-like format
          translatedText = responseBody.outputs[0].text;
          inputTokens = responseBody.meta?.tokens?.input_tokens || 0;
          outputTokens = responseBody.meta?.tokens?.output_tokens || 0;
        } else if (responseBody.text) {
          // Simple text format
          translatedText = responseBody.text;
          inputTokens = responseBody.usage?.input_tokens || 0;
          outputTokens = responseBody.usage?.output_tokens || 0;
        } else {
          // Fallback - try to find text in response
          translatedText = JSON.stringify(responseBody);
          console.warn('🟡 Unknown response format, using raw response');
        }
      }

      // Update token tracking
      this.tokenUsage.totalInputTokens += inputTokens;
      this.tokenUsage.totalOutputTokens += outputTokens;
      this.tokenUsage.totalRequests += 1;
      
      // Update per-model tracking
      const modelKey = this.selectedModel;
      if (!this.tokenUsage.perModel[modelKey]) {
        this.tokenUsage.perModel[modelKey] = {
          inputTokens: 0,
          outputTokens: 0,
          requests: 0,
          totalCost: 0,
          modelName: this.modelConfig.name
        };
      }
      
      this.tokenUsage.perModel[modelKey].inputTokens += inputTokens;
      this.tokenUsage.perModel[modelKey].outputTokens += outputTokens;
      this.tokenUsage.perModel[modelKey].requests += 1;
      
      // Calculate per-model cost
      const modelInputCost = (inputTokens / 1000) * this.modelConfig.inputCostPer1K;
      const modelOutputCost = (outputTokens / 1000) * this.modelConfig.outputCostPer1K;
      this.tokenUsage.perModel[modelKey].totalCost += modelInputCost + modelOutputCost;
      
      this.tokenUsage.requestHistory.push({
        timestamp: new Date(),
        inputTokens,
        outputTokens,
        model: this.selectedModel,
        isRealTime,
        customPrompt: !!this.customSystemPrompt
      });

      console.log('🟢 Translation successful');
      console.log('🔵 Input tokens:', inputTokens);
      console.log('🔵 Output tokens:', outputTokens);
      console.log('🔵 Translated text:', translatedText);

      return translatedText.trim();

    } catch (error) {
      console.error('🔴 Translation error:', error);
      console.error('🔴 Model ID:', this.modelConfig.modelId);
      
      if (error.name === 'ValidationException') {
        throw new Error(`Invalid request format for ${this.modelConfig.name}. This may be a model configuration issue. Please try Claude 3.5 Haiku as a fallback.`);
      } else if (error.name === 'AccessDeniedException') {
        throw new Error('Access denied. Please check your AWS permissions for Bedrock.');
      } else if (error.name === 'ThrottlingException') {
        throw new Error('Request throttled. Please wait a moment and try again.');
      } else if (error.name === 'ServiceQuotaExceededException') {
        throw new Error('Service quota exceeded. Please check your Bedrock usage limits.');
      } else if (error.message && error.message.includes('credentials')) {
        throw new Error('AWS credentials expired or invalid. Please re-authenticate.');
      } else {
        throw new Error(`Translation failed: ${error.message || 'Unknown error'}`);
      }
    }
  }

  // Get current token usage statistics
  getTokenUsage() {
    const sessionDurationMs = new Date() - this.tokenUsage.sessionStartTime;
    const sessionDurationMinutes = Math.round(sessionDurationMs / 60000);

    // Calculate estimated costs
    const inputCost = (this.tokenUsage.totalInputTokens / 1000) * this.modelConfig.inputCostPer1K;
    const outputCost = (this.tokenUsage.totalOutputTokens / 1000) * this.modelConfig.outputCostPer1K;
    const totalCost = inputCost + outputCost;

    return {
      totalInputTokens: this.tokenUsage.totalInputTokens,
      totalOutputTokens: this.tokenUsage.totalOutputTokens,
      totalRequests: this.tokenUsage.totalRequests,
      sessionDurationMinutes,
      currentModel: {
        name: this.modelConfig.name,
        provider: this.modelConfig.provider,
        costTier: this.modelConfig.costTier
      },
      estimatedCost: {
        inputCost,
        outputCost,
        totalCost
      },
      perModel: this.tokenUsage.perModel || {},
      requestHistory: this.tokenUsage.requestHistory
    };
  }

  // Reset token usage statistics
  resetTokenUsage() {
    this.tokenUsage = {
      totalInputTokens: 0,
      totalOutputTokens: 0,
      totalRequests: 0,
      sessionStartTime: new Date(),
      requestHistory: []
    };
    console.log('🔵 Token usage statistics reset');
  }
}
